<template>
  <q-toolbar :class="['header-toolbar', toolbarClass]">
    <div class="toolbar-layout full-width">

      <!-- Left: Title -->
      <div class="tlayout-title row items-center no-wrap">
        <span class="app-title text-weight-bold tracking-tight">
          TUS <span class="text-weight-light text-muted">Calculator</span>
        </span>
        <q-chip
          v-if="environment === 'pages'"
          color="negative"
          outline
          dense
          square
          class="text-bold text-caption q-ml-sm dev-chip"
        >
          DEV
        </q-chip>
      </div>

      <!-- Center: All brand logos — wraps to second row on xs -->
      <div class="tlayout-logos row items-center no-wrap">
        <q-img
          :src="fusLogo"
          alt="FUS logo"
          height="auto"
          class="fus-logo brand-logo"
        />
        <div class="logo-rule" />
        <div class="academic-partners row items-center no-wrap">
          <q-img
            :src="currentLogo"
            alt="Radboud University logo"
            height="auto"
            fit="contain"
            class="ru-logo brand-logo"
          />

          <div class="logo-rule" />
          <div class="stanford-brand" :class="isDarkModeGlobal ? 'text-white' : 'text-grey-9'">
            <span class="stanford-text">Stanford</span>
            <span class="medicine-text">Medicine</span>
          </div>
        </div>
      </div>

      <!-- Right: Action buttons -->
      <div class="tlayout-actions row items-center no-wrap action-hub">
        <about-message />
        <disclaimer-message />
      </div>

    </div>
  </q-toolbar>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useCalculatorStore } from '../stores/calculator.store'
import logoUrl from '../assets/Logo_RU_EN_RGB.png'
import logoWhiteUrl from '../assets/RU_logo_NL_wit.png'
import fusLogoUrl from '../assets/logo_FUS_CENTRE.png'
import fusLogoDarkUrl from '../assets/logo_FUS_CENTRE_dark.png'
import DisclaimerMessage from '../components/DialogDisclaimerMessage.vue'
import AboutMessage from '../components/DialogAboutMessage.vue'

const environment = import.meta.env.VITE_APP_ENV
const calculatorStore = useCalculatorStore()
const { isDarkModeGlobal } = storeToRefs(calculatorStore)

const currentLogo = computed(() => isDarkModeGlobal.value ? logoWhiteUrl : logoUrl)
const fusLogo = computed(() => isDarkModeGlobal.value ? fusLogoDarkUrl : fusLogoUrl)

const toolbarClass = computed(() =>
  isDarkModeGlobal.value
    ? 'bg-neutral-dark text-grey-1'
    : 'bg-glass text-grey-9'
)
</script>

<style scoped>
/* ── Premium Layout Base ── */
.header-toolbar {
  min-height: 64px;
  padding: 0 24px;
  border-bottom: 1px solid v-bind("isDarkModeGlobal ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'");
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* ── Responsive Grid: 3-column on sm+, 2-column + wrap on xs ── */
.toolbar-layout {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-template-areas: "title logos actions";
  align-items: center;
}

.tlayout-title   { grid-area: title; }
.tlayout-logos   { grid-area: logos; gap: 16px; justify-self: center; }
.tlayout-actions { grid-area: actions; justify-self: end; }

/* ── Typography ── */
.app-title {
  font-size: 1.25rem;
  letter-spacing: -0.02em;
}
.text-muted { opacity: 0.7; }
.tracking-tight { letter-spacing: -0.03em; }

.dev-chip {
  font-size: 0.65rem !important;
  letter-spacing: 0.05em;
  padding: 0 6px;
  height: 18px;
}

/* ── Logo Sizes ── */
.fus-logo  { width: 190px; flex-shrink: 0; }
.ru-logo   { width: 184px; flex-shrink: 0; }

.brand-logo {
  transition: filter 0.3s ease, transform 0.2s ease;
}
.brand-logo:hover { transform: scale(1.02); }

/* ── Subtle divider between FUS and academic partners ── */
.logo-rule {
  flex-shrink: 0;
  width: 2px;
  height: 28px;
  background: v-bind("isDarkModeGlobal ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'");
}

/* ── Spacing between RU logo and Stanford brand ── */
.academic-partners {
  gap: 16px;
}

/* ── Stanford Branding ── */
.stanford-brand {
  display: flex;
  flex-direction: column;
  line-height: 1.05;
  user-select: none;
}
.stanford-text {
  font-family: 'Source Serif 4', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 600;
  color: v-bind("isDarkModeGlobal ? '#ffffff' : '#8C1515'");
}
.medicine-text {
  font-family: 'Source Sans 3', Helvetica, sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  opacity: 0.75;
}

/* ── Action Hub ── */
.action-hub :deep(.q-btn) {
  border-radius: 8px;
  transition: background-color 0.2s;
}

/* ── XS: logos drop to a full-width second row, always visible ── */
@media (max-width: 599px) {
  .header-toolbar {
    min-height: unset;
    padding: 8px 12px 12px;
  }

  .toolbar-layout {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "title   actions"
      "logos   logos";
    row-gap: 10px;
  }

  .tlayout-logos {
    justify-self: center;
    gap: 10px;
  }

  .fus-logo  { width: 100px; }
  .ru-logo   { width: 110px; }

  .academic-partners { gap: 12px; }

  .stanford-text { font-size: 0.9rem; }
  .medicine-text { font-size: 0.65rem; }
}
</style>

